import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Activity, Clock, Folder, Lock, PlugZap, Users } from 'lucide-react';
import { BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
import { AskLoop } from './AskLoop';
import CompanyBrain from '../../CompanyBrain.jsx';

/**
 * The simplest form of the idea: a row of sources as bare circles, tracks as
 * columns below, and observations visibly travelling from one to the other.
 *
 * No labels or cadences on the sources — the logo is the whole point, and the
 * text belongs on the observation that comes out. No outer panel either, so
 * the diagram sits on the page rather than inside a box.
 */

type Observation = {
  id: string;
  logo: string;
  text: string;
};
/**
 * Two groups, each in its own oval: what Giga sweeps on a schedule, and what
 * your team says to it. Grouping is the only labelling the sources need — it
 * makes clear the memory has two independent origins.
 */
const SOURCE_GROUPS = [{
  label: 'Scans',
  note: 'On a schedule',
  caption: 'Sweep your tools on a schedule.',
  // Every mark here resolves to a real vector rather than a 64px favicon, so
  // the row sits at one optical weight instead of mixing crisp logos with
  // soft rasters.
  logos: ['gmail', 'salesforce', 'notion', 'intercom', 'linear']
}, {
  label: 'Team chats',
  note: 'As people work',
  caption: 'Adds what it learns from conversations.',
  // Five marks, matching Scans — the two ovals are siblings, so an uneven
  // count made them read as unequal in importance rather than in kind.
  logos: ['slack', 'microsoftteams', 'giga', 'chatgpt', 'claude']
}];
const TRACKS = [{
  id: 'northwind',
  name: 'Northwind Labs',
  group: 'Sales',
  shared: true,
  count: 412,
  seed: [{
    id: 'nw-1',
    logo: 'notion',
    text: 'Champion unchanged after the pilot review'
  }, {
    id: 'nw-2',
    logo: 'salesforce',
    text: 'Opportunity in Legal review · $84k'
  }]
}, {
  id: 'onboarding',
  name: 'Onboarding revamp',
  group: 'Customer Success',
  shared: true,
  count: 268,
  seed: [{
    id: 'ob-1',
    logo: 'intercom',
    text: 'Setup is the top blocker, 31 conversations'
  }, {
    id: 'ob-2',
    logo: 'linear',
    text: 'CSV import bug shipped Tuesday'
  }]
}, {
  id: 'pipeline',
  name: 'Q3 pipeline',
  group: 'Marketing',
  shared: true,
  count: 631,
  seed: [{
    id: 'pl-1',
    logo: 'gmail',
    text: 'Four renewal threads quiet over a week'
  }, {
    id: 'pl-2',
    logo: 'slack',
    text: 'Discount ceiling agreed at 15%'
  }]
}, {
  id: 'board',
  name: 'Board reporting',
  group: 'Leadership',
  shared: false,
  count: 88,
  seed: [{
    id: 'bd-1',
    logo: 'salesforce',
    text: 'Q3 forecast down one slipped deal'
  }]
}];
type Event = {
  source: string;
  track: string;
  text: string;
  note?: string;
};
const EVENTS: Event[] = [{
  source: 'salesforce',
  track: 'pipeline',
  text: 'Acme moved to Legal review · closes 12 Sep'
}, {
  source: 'slack',
  track: 'northwind',
  text: 'Dana wants to be re-engaged in September',
  note: 'Marcus in #sales'
}, {
  source: 'notion',
  track: 'northwind',
  text: 'Pilot pushed to Q4 — budget freeze, not a loss'
}, {
  source: 'intercom',
  track: 'onboarding',
  text: 'Import errors now the second-most cited issue'
}, {
  source: 'giga',
  track: 'onboarding',
  text: 'We quote onboarding at 14 days, not 30',
  note: 'Marcus, Giga for web'
}, {
  source: 'gmail',
  track: 'pipeline',
  text: 'Harbour renewal quiet for nine days'
}, {
  source: 'claude',
  track: 'onboarding',
  text: 'Import runs behind a queue — retries are safe',
  note: 'Engineering, Claude Code'
}, {
  source: 'linear',
  track: 'onboarding',
  text: 'Setup wizard rewrite merged to main'
}, {
  source: 'chatgpt',
  track: 'board',
  text: 'Board wants NRR framed against logo churn',
  note: 'Sarah, ChatGPT'
}, {
  source: 'salesforce',
  track: 'board',
  text: 'Committed ARR up $46k this week'
}];
const TICK_MS = 2000;
const FLIGHT_MS = 900;

/**
 * The four numbers that describe the brain's current state, counted up once on
 * entry. They close the section the way the diagram opens it: the diagram shows
 * one observation arriving, these show what all of them add up to.
 */
const STATS = [{
  icon: PlugZap,
  value: 9,
  decimals: 0,
  suffix: '',
  label: 'Connected sources'
}, {
  icon: Folder,
  value: 4,
  decimals: 0,
  suffix: '',
  label: 'Live tracks'
}, {
  icon: Activity,
  value: 1.4,
  decimals: 1,
  suffix: 'k',
  label: 'Observations kept'
}, {
  icon: Clock,
  value: 15,
  decimals: 0,
  suffix: 'min',
  label: 'Median freshness'
}] as const;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Counts to its target once, when it first comes into view. A number that
 * animates every time it is scrolled past stops reading as a fact.
 */
function CountUp({
  value,
  decimals,
  suffix,
  delay
}: {
  value: number;
  decimals: number;
  suffix: string;
  delay: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? value : 0);
  useEffect(() => {
    if (reduce) {
      setShown(value);
      return;
    }
    const node = ref.current;
    if (!node) return;
    let raf = 0;
    let timer: number | undefined;
    const observer = new IntersectionObserver(entries => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      timer = window.setTimeout(() => {
        const start = performance.now();
        const duration = 1500;
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          setShown(value * easeOutCubic(t));
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      }, delay);
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (timer) window.clearTimeout(timer);
    };
  }, [value, delay, reduce]);
  return <span ref={ref} className="tabular-nums" data-id="element-605" data-mp="1c2d1h8d06y0t">
      {shown.toFixed(decimals)}
      {suffix}
    </span>;
}
export function BrainFlowExperiment() {
  const reduce = useReducedMotion();
  const shell = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const trackRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const railRef = useRef<HTMLDivElement>(null);
  const step = useRef(0);
  const [flight, setFlight] = useState<{
    key: number;
    from: {
      x: number;
      y: number;
    };
    to: {
      x: number;
      y: number;
    };
    event: Event;
  } | null>(null);
  // The id of the observation that has JUST landed. Only that one is white,
  // and only for a moment — at rest every row is a settled dark row, so the
  // bright card marks the arrival rather than permanently flagging the top of
  // each list.
  const [freshId, setFreshId] = useState<string | null>(null);
  // The track that just took delivery. Kept for as long as the landed card is
  // still cooling, so the spotlight does not snap off at the exact moment the
  // observation arrives — which is the moment the reader is looking at it.
  const [landedTrack, setLandedTrack] = useState<string | null>(null);
  const [contents, setContents] = useState<Record<string, Observation[]>>(() => Object.fromEntries(TRACKS.map(t => [t.id, t.seed])));
  const [counts, setCounts] = useState<Record<string, number>>(() => Object.fromEntries(TRACKS.map(t => [t.id, t.count])));
  useEffect(() => {
    if (reduce) return;
    let landing: number | undefined;
    let cooling: number | undefined;
    let aiming: number | undefined;
    const interval = window.setInterval(() => {
      const event = EVENTS[step.current % EVENTS.length];
      step.current += 1;
      const key = step.current;
      /*
       * THE RAIL FOLLOWS THE INCOMING OBSERVATION.
       *
       * On a phone the tracks are a carousel, so the destination is often
       * off-screen — the card would fly out past the panel's edge to a track
       * nobody can see. Centring the target track first makes the carousel
       * read as being driven by the ingest rather than by the reader, and the
       * card always lands somewhere visible. Measurement waits for the scroll
       * to finish, otherwise the flight aims at where the card used to be.
       */
      const rail = railRef.current;
      const card = trackRefs.current[event.track];
      let settle = 0;
      if (rail && card && rail.scrollWidth > rail.clientWidth + 4) {
        const r = rail.getBoundingClientRect();
        const c = card.getBoundingClientRect();
        const delta = c.left + c.width / 2 - (r.left + r.width / 2);
        if (Math.abs(delta) > 4) {
          rail.scrollTo({
            left: rail.scrollLeft + delta,
            behavior: 'smooth'
          });
          settle = 420;
        }
      }
      aiming = window.setTimeout(() => launch(event, key), settle);
    }, TICK_MS);
    const launch = (event: Event, key: number) => {
      const box = shell.current?.getBoundingClientRect();
      const a = sourceRefs.current[event.source]?.getBoundingClientRect();
      const b = trackRefs.current[event.track]?.getBoundingClientRect();
      if (!box || !a || !b) return;
      setFlight({
        key,
        from: {
          x: a.left - box.left + a.width / 2,
          y: a.bottom - box.top
        },
        // Aim at the top of the list, where the new observation will appear.
        to: {
          x: b.left - box.left + b.width / 2,
          y: b.top - box.top + 66
        },
        event
      });
      landing = window.setTimeout(() => {
        setContents(prev => ({
          ...prev,
          [event.track]: [{
            id: `${key}`,
            logo: event.source,
            text: event.text
          }, ...prev[event.track]].slice(0, 3)
        }));
        setCounts(prev => ({
          ...prev,
          [event.track]: prev[event.track] + 1
        }));
        setFlight(null);
        setFreshId(`${key}`);
        setLandedTrack(event.track);
        // It stays white just long enough to be noticed, then cools into the
        // settled rows. The 700ms colour transition on the row does the fade.
        if (cooling) window.clearTimeout(cooling);
        cooling = window.setTimeout(() => {
          setFreshId(null);
          setLandedTrack(null);
        }, 900);
      }, FLIGHT_MS);
    };
    return () => {
      window.clearInterval(interval);
      if (aiming) window.clearTimeout(aiming);
      if (landing) window.clearTimeout(landing);
      if (cooling) window.clearTimeout(cooling);
    };
  }, [reduce]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  /*
   * THE SPOTLIGHT.
   *
   * One track is receiving at a time, and until now the only thing marking it
   * was a slightly lighter fill — invisible next to three identical siblings.
   * Holding the focus from the moment the card launches through to the moment
   * it has cooled, and pushing the other three back, means the eye is already
   * on the destination before the observation gets there.
   *
   * Deliberately null between deliveries: at rest all four sit at equal weight,
   * so the dimming reads as attention moving rather than as three tracks being
   * permanently disabled.
   */
  const focusTrack = flight?.event.track ?? landedTrack;
  return <div className="mx-auto max-w-[1120px]" data-id="element-606" data-mp="3nklnfjjzgrnw">
      {/* One centred composition on a dark stage rather than an eyebrow over a
       diagram: a source row that stands in for the eyebrow, the claim at full
       display size, the lead, one action — then the diagram, then the numbers
       it adds up to. The label the section used to carry is now the pill, so
       the same information arrives as evidence instead of as a caption. */}
      {/* The brain sits BESIDE the claim rather than inside the diagram: it is
       the section's subject, so it belongs with the statement, and the console
       below is then free to be nothing but the machinery. */}
      {/* Stacked until md: between 640 and 768 the brain beside the claim left
       the headline in a column too narrow to hold two lines. */}
      <SectionIntro className="relative flex flex-col items-start gap-6 md:flex-row md:items-start md:justify-between md:gap-10" data-id="element-607" data-mp="47wz6qf73d7k">
        <div className="relative z-10" data-id="element-608" data-mp="3vezr5k2uxniu">
          {/* Scaled up to sit with the page's opening claim rather than with
           its section headings: this is the second-largest statement on the
           site, and at section size it was outranked by the diagram below it.
           Fluid, so both lines stay on one row as the column narrows. */}
          <h2 className="font-title text-[clamp(32px,8.4vw,80px)] font-bold leading-[1.05] tracking-[-0.03em] text-[#FAF8F4] md:text-[clamp(44px,6.2vw,80px)]" data-id="element-609" data-mp="2fzk9wf0vbq8r">
            Never wait for
            <br data-id="element-610" data-mp="qh41te0hmrso" />
            information again<span className="text-[#EA580C]" data-id="element-611" data-mp="vf5o07t7n78d">.</span>
          </h2>
          {/* The claim needs its subject named: without this the headline is a
           promise with no product behind it, and the console below is left to
           explain both what a company brain is AND how it stays current. */}
          <p className="mt-4 max-w-[46ch] text-[16px] leading-relaxed text-[#C9BFB4] md:mt-5 md:text-[17px]" data-id="element-612" data-mp="7q2ykdsc4a3b">
            Giga holds your entire knowledge base — everything your team knows
            and everything your tools record — in one place your people and your
            AI can both ask.
          </p>
        </div>

        {/* The brain is the section's SUBJECT, so it sits with the claim at a
         size that reads as an image rather than an icon — and it is cropped by
         the section's right edge, which makes it feel like an object the page
         is looking at part of rather than a picture placed in a slot. */}
        {/* Pushed further right and dropped below the headline's cap line.
         Vertically centring it against the copy tied it to the text block, so
         it read as a diagram belonging to the headline; sitting lower and
         cropped harder by the section's right edge, it reads as an object the
         page happens to be looking at part of — which is the whole point of a
         brain the company owns rather than an illustration of one. */}
        {/* OUT OF FLOW from md up. This was the empty band: the brain is far
         taller than the copy beside it, and as a flex child it set the row's
         height — so the left column ended after three lines and the remaining
         ~250px was blank, then the console started below all of it. Absolutely
         positioned, the row is sized by the COPY and the brain overhangs into
         the gap above the console instead of creating one. Seated slightly
         below centre and cropped by the right edge, so it still reads as part
         of a larger object rather than a diagram bolted to the headline. */}
        {/* ON PHONES IT LEADS, AND IT BLEEDS. Below the copy it was the last
         thing in the section's opening and read as a footnote to the claim;
         above it, it is what the claim is about — you see the brain, then read
         what it does. At roughly twice its previous size it also runs past
         both gutters, so it reads as an object the screen is a window onto,
         which is the same effect the desktop crop achieves from the right.
         From md up it returns to being out of flow beside the copy. */}
        <div className="pointer-events-none relative -mx-6 -mb-4 -mt-2 order-first flex shrink-0 justify-center overflow-hidden md:absolute md:-right-10 md:top-[60%] md:order-none md:mx-0 md:mb-0 md:mt-0 md:block md:self-center md:overflow-visible md:-translate-y-1/2 lg:-right-16" aria-hidden="true" data-id="element-613" data-mp="s9ihl9t6jb1v">
          {/* The plate already ships light-on-transparent, so no inversion and
           no screen blend — both were there to lift a dark-on-white original
           off the ground, and applied here they would only wash it out. */}
          {/* The static plate is replaced by the CompanyBrain component, which
           renders the same artwork plus a canvas overlay of perpetual dot
           motion — the brain now has a life of its own at rest rather than
           only reacting to the console below. */}
          {/* A soft orange bloom while an observation is crossing below — the
           brain lights up as the memory it stands for takes something in.
           It is FIRST in the DOM so it paints behind the artwork: as a sibling
           after it, the bloom sat over the plate and read as a coloured film
           on top of the brain rather than light coming from behind it. */}
          {/* It fires on every THIRD observation, not every one, and swells
           over 1.4s instead of 700ms. At one bloom every two seconds it read
           as a blinking indicator; at one every six, softer and slower, it
           reads as the brain occasionally stirring. */}
          <div className={`absolute left-1/2 top-[46%] size-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FB923C]/[0.09] blur-3xl transition-opacity duration-[1400ms] ease-out ${flight && flight.key % 3 === 0 ? 'opacity-100' : 'opacity-0'}`} data-id="element-614" data-mp="2hsv12p2fczu1" />
          {/* Sized well past its column and cropped by the section's right
           edge — at 400px it read as an illustration beside the headline; at
           this scale it reads as the section's subject, with the page looking
           at part of a much larger object. */}
          {/* Brought down again: out of flow it overhangs the gap below, and
           at 600px the overhang was landing on the console's top edge. */}
          {/* The "company brain" wordmark is baked into the artwork, below the
           brain itself, so it cannot be removed as an element — it is cropped
           off with a hard mask instead. Cut at 73% of the plate's height: the
           dissolving dots below the brain end around 70%, and the lettering
           starts around 77%. Masking the wrapper takes the canvas overlay's
           type-sparkle particles with it, so no stray dots are left behind. */}
          <div className="w-[152%] max-w-none [mask-image:linear-gradient(to_bottom,#000_0,#000_73%,transparent_73%)] md:w-[420px] lg:w-[480px]" data-id="element-615" data-mp="15jbzx1s3j0h5">
            <CompanyBrain width="100%" data-id="element-616" data-mp="2267706m61hcr" />
          </div>
        </div>
      </SectionIntro>

      {/* The diagram now sits inside an INSTRUMENT rather than on a dark
       rectangle. Nothing in the diagram changed — what changed is the frame
       around it: a bordered console with corner ticks, a faint measuring
       grid, a live status rail across the top and the readings along the
       bottom. The claim is that this thing is running right now, so the
       section should look like something being monitored, not like a picture
       of a system. */}
      {/* A solid ground, not a transparent frame. The console is meant to read
       as a piece of equipment sitting on the page; letting the section's ember
       wash through it made it look like an overlay and left the cards it
       contains floating with nothing behind them. */}
      {/* AMBIENT HALO. Cast as box-shadow rather than as a layer or a gradient:
       a shadow paints outside the border box, so the light appears to come from
       behind the console's edges and cannot tint the container, the background,
       or anything inside it. Three stacked casts, all heavily feathered — a
       broad warm brown pool below, a tighter orange-leaning one at the lower
       edges, and a faint ring with no offset so the top is only just touched.
       The container's own fill, border and dark drop shadow are unchanged. */}
      {/* Enough clearance for the lead's last line: with the brain out of flow
       the row is only as tall as the copy, so the console was landing right up
       against "company brain" instead of below it. */}
      <div className="relative mt-14 overflow-hidden rounded-[20px] border border-white/[0.14] bg-[#1C1511] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_40px_100px_-50px_rgba(0,0,0,0.9),0_0_90px_-10px_rgba(122,74,46,0.20),0_46px_130px_-34px_rgba(140,84,50,0.42),0_28px_80px_-26px_rgba(190,104,52,0.26)] sm:rounded-[28px] md:mt-16" data-id="element-617" data-mp="1e4jfo6uf8wkb">
        {/* Measuring grid — hairlines, not a texture. It gives the frame a
         sense of being a surface with coordinates. */}
        {/* Behind the content, not over it. At z-2 the grid was painting on top
         of the track cards, which is what made solid cards look translucent. */}
        {/* Faded further, and faded UNEVENLY: a flat 5% grid drew hard lines
         straight through the middle of the console where the cards and the
         flight path are. Held at 3% and masked so it is strongest at the top
         and gone by the lower third, it gives the frame its coordinates
         without ruling lines across the part being read. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-[0.03] [mask-image:linear-gradient(to_bottom,#000,rgba(0,0,0,0.35)_55%,transparent_88%)]" style={{
        backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
        backgroundSize: '56px 56px'
      }} data-id="element-618" data-mp="4y503nsqzvg" />

        {/* A single slow sweep down the frame. Continuous, so linear, and the
         only thing on the page that moves without being told to — it reads as
         the instrument taking a pass rather than as decoration. */}
        {/* IT PASSES OVER, NOT BEHIND. It used to sit under the content, so a
         line that read as the instrument scanning the whole frame was chopped
         into segments by every opaque track card it reached — visible in the
         gaps, gone across the cards. Lifted above the content and dropped to a
         fraction of its previous strength with a soft falloff either side, it
         crosses everything as one continuous, barely-there pass. Screen blend
         keeps it light on the plate rather than a solid orange bar. */}
        {!reduce && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 z-[2] h-[3px] mix-blend-screen" style={{
        background: 'linear-gradient(to bottom, transparent, rgba(251,146,60,0.14), transparent)'
      }} initial={{
        top: '0%'
      }} animate={{
        top: ['0%', '100%']
      }} transition={{
        duration: 11,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-619" data-mp="2m6kjdvak9l5u" />}

        {/* Corner ticks — registration marks. Four small accents instead of a
         heavier border. */}
        {[['left-3 top-3 border-l border-t', ''], ['right-3 top-3 border-r border-t', ''], ['left-3 bottom-3 border-l border-b', ''], ['right-3 bottom-3 border-r border-b', '']].map(([pos]) => <span key={pos} aria-hidden="true" className={`pointer-events-none absolute size-3 border-[#FB923C]/45 ${pos}`} data-id="element-620" data-mp="1ygq6jdq8rwll" />)}

        {/* Status rail. */}
        <div className="relative flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-5 sm:py-3.5 md:px-8" data-id="element-621" data-mp="1astn5yu80l4n">
          <span className="flex items-center gap-2.5 whitespace-nowrap text-[9.5px] font-bold uppercase tracking-[0.12em] text-white/60 sm:gap-3 sm:text-[10.5px] sm:tracking-[0.14em]" data-id="element-622" data-mp="1oox1sjsm2kw0">
            <span className="relative flex size-1.5 items-center justify-center" data-id="element-623" data-mp="1ynmocpvgctil">
              {!reduce && <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#FB923C] opacity-75" data-id="element-624" data-mp="1tq4acl2kvo3q" />}
              <span className="relative inline-flex size-1.5 rounded-full bg-[#FB923C]" data-id="element-625" data-mp="27jlti4rcqyt8" />
            </span>
            Ingest live
          </span>
          <span className="flex items-center gap-3.5 text-right text-[9.5px] tabular-nums text-white/40 sm:gap-5 sm:text-[10.5px]" data-id="element-626" data-mp="2hf5kqduz19bn">
            <span className="hidden sm:inline" data-id="element-627" data-mp="2cip0huqw7f3u">Cadence 15m</span>
            <span aria-hidden="true" className="hidden h-3 w-px bg-white/15 sm:inline-block" data-id="element-628" data-mp="2wjruyv63ur18" />
            <span data-id="element-629" data-mp="30jsakj4xx89v">{total.toLocaleString()} observations kept</span>
          </span>
        </div>

      <div ref={shell} className="relative z-[1] px-4 py-7 sm:px-5 sm:py-8 md:px-8 md:py-10" data-id="element-630" data-mp="35b0l5ii0azom">
        {/* THE ASK LEADS. What the reader wants is the answer, not the
         plumbing — so the thing you talk to comes first, and everything below
         it is the explanation of where its answers come from. */}
        {/* No heading. The field performs the claim instead of announcing it:
         a question is typed, the field resolves into thinking, and the answer
         writes itself back. A title above that would only spoil it. */}
        <AskLoop data-id="element-631" data-mp="2rl6xv4m5gnh0" />


        {/* Sources, grouped by origin. */}
        <div className="flex flex-wrap items-start justify-center gap-6 md:gap-10" data-id="element-632" data-mp="1tif9glso2qu">
          {SOURCE_GROUPS.map(group => <div key={group.label} className="flex flex-col items-center" data-id="element-633" data-mp="6sd532i6irkg">
            <div className="relative rounded-[999px] border border-dashed border-white/20 px-2.5 pb-2 pt-3.5 md:px-3.5" data-id="element-634" data-mp="1rbytkzcrpxfd">
              <span className="absolute -top-[9px] left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#1C1511] px-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/50" data-id="element-635" data-mp="1mdx9cb3lvs2b">
                {group.label}
                <span className="ml-1.5 font-medium normal-case tracking-normal text-white/30" data-id="element-636" data-mp="1cm9nqqtqd58r">
                  {group.note}
                </span>
              </span>
              {/* SMALLER ON PHONES SO ALL FIVE HOLD ONE ROW. At 48px they
               wrapped 4 + 1, and a lone fifth mark centred beneath the others
               read as an odd one out rather than as a member of the group —
               the oval's whole job is to say these belong together. */}
              <div className="flex flex-nowrap items-center justify-center gap-1.5 sm:flex-wrap sm:gap-2.5 md:gap-3" data-id="element-637" data-mp="2szzuk5c8i7m3">
                {group.logos.map(s => {
                  const firing = flight?.event.source === s;
                  // The source is the origin marker: while its observation is in
                  // flight it swells and lights up, and settles back once the card
                  // has landed. That reads as "this one just sent something"
                  // without drawing a line to say so.
                  return <div key={s} ref={el => {
                    sourceRefs.current[s] = el;
                  }} className={`flex size-[38px] items-center justify-center rounded-full border bg-[#FAF8F4] transition-[border-color,box-shadow,transform] duration-300 ease-out sm:size-12 md:size-14 ${firing ? 'scale-110 border-[#FB923C] shadow-[0_0_0_5px_rgba(251,146,60,0.22),0_0_28px_rgba(251,146,60,0.35)]' : 'scale-100 border-white/15'}`} data-id="element-638" data-mp="heqy0b6s5azy">
                      <BrandLogo slug={s} className="size-4 sm:size-5 md:size-6" data-id="element-639" data-mp="106a1ej4pur2i" />
                    </div>;
                })}
              </div>
            </div>
            {/* Each origin says what it does, directly under the tools it is
             made of — the oval alone named the group without explaining it. */}
            {/* No character cap and no wrapping from sm up: the caption is a
             single short line, and a measure narrow enough to break it made it
             read as two ragged fragments under the oval. */}
            <p className="mt-3 text-center text-[12.5px] leading-snug text-white/50 sm:whitespace-nowrap" data-id="element-640" data-mp="3i9gjthdf8tnc">
              {group.caption}
            </p>
          </div>)}
        </div>

        {/* THE DESTINATION. The brain sits in the crossing space between the
         tools and the tracks, so the flight reads as a path with a middle:
         an observation leaves a tool, passes THROUGH the brain, and lands in
         a track. Inverted and screened so its black dot matrix reads as light
         on the dark ground, and it brightens for as long as something is in
         flight — the brain is only lit when it is taking something in. */}
        {/* Crossing space — only the observation in flight belongs here. */}
        <div className="h-20 md:h-24" aria-hidden="true" data-id="element-641" data-mp="1lcjn2nuyxinl" />

        <div className="mb-2 flex items-center justify-between gap-2" data-id="element-642" data-mp="1qal2myt6qzub">
          <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-white/45" data-id="element-643" data-mp="1qcp6xhuzy0v4">
            <Folder size={11} strokeWidth={2.2} data-id="element-644" data-mp="1zbayg2ig4q81" />
            Tracks
          </p>
        </div>

        {/* ONE ROW THAT PEEKS ON A PHONE, a grid from sm up.
         Four full-width cards stacked made four screens of near-identical
         panels — the reader scrolls past three of them without learning
         anything new, because the point is that tracks are a SET, not what
         any one of them contains. Held in a snapping rail at 78% width, the
         neighbours stay visible at both edges, so the set is legible in one
         glance and the depth of it is implied rather than paid for in
         scroll. */}
        <div ref={railRef} className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4" data-id="element-645" data-mp="2rzf2kjldcv8m">
          {TRACKS.map(t => {
            const lit = focusTrack === t.id;
            // Only pushed back while ANOTHER track holds the light. With no
            // delivery in progress every card returns to the same weight.
            const dimmed = focusTrack !== null && !lit;
            return <div key={t.id} ref={el => {
              trackRefs.current[t.id] = el;
            }} className={`w-[78%] shrink-0 snap-center rounded-2xl border p-3.5 transition-[background-color,border-color,box-shadow,opacity,transform] duration-300 ease-out sm:w-auto sm:shrink ${lit ? 'z-[1] scale-[1.015] border-white/25 bg-white/[0.09] opacity-100 shadow-[0_0_0_1px_rgba(251,146,60,0.14),0_18px_44px_-18px_rgba(0,0,0,0.85)]' : dimmed ? 'scale-100 border-white/[0.06] bg-white/[0.02] opacity-40 shadow-none' : 'scale-100 border-white/10 bg-white/[0.05] opacity-100 shadow-none'}`} data-id="element-646" data-mp="z1t3e7g73ozn">
              <div className="flex items-start gap-2" data-id="element-647" data-mp="15tm2ow3ch2oc">
                <Folder size={14} strokeWidth={1.9} className="mt-0.5 shrink-0 text-white/40" data-id="element-648" data-mp="15w8amwu5dk73" />
                <div className="min-w-0 flex-1" data-id="element-649" data-mp="11un19lgtno1n">
                  <p className="truncate text-[13px] font-semibold text-white" data-id="element-650" data-mp="1fslcwj3rd6dg">
                    {t.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 truncate text-[11px] text-white/50" data-id="element-651" data-mp="1arnm5i4on40c">
                    {t.shared ? <Users size={9} data-id="element-652" data-mp="1oqx0sdd8pq27" /> : <Lock size={9} data-id="element-653" data-mp="1kregnjt8tj0f" />}
                    {t.group}
                  </p>
                </div>
              </div>

              <p className="mt-2.5 flex items-baseline gap-1.5 border-t border-white/10 pt-2.5" data-id="element-654" data-mp="1yj2qfgg0rve8">
                <motion.span key={counts[t.id]} initial={{
                  opacity: 0.4,
                  y: -3
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.24
                }} className="font-title text-[17px] font-bold tabular-nums text-white" data-id="element-655" data-mp="1tjgdl843sh5y">
                  {counts[t.id].toLocaleString()}
                </motion.span>
                <span className="text-[11px] text-white/45" data-id="element-656" data-mp="2n47kjv8ibzyv">
                  observations
                </span>
              </p>

              {/* Fixed height, fixed-height rows: the list is constantly
               swapping items of different lengths, and letting it size to its
               contents pushed the entire rest of the page up and down. */}
              <ul className="mt-2 h-[156px] space-y-1.5 overflow-hidden" data-id="element-657" data-mp="23fgk1q711pb3">
                <AnimatePresence initial={false} data-id="element-658" data-mp="286f676ep5hm9">
                  {contents[t.id].map((o, i) => {
                    const fresh = o.id === freshId;
                    return <motion.li key={o.id} layout initial={{
                      opacity: 0,
                      scale: 0.96,
                      y: -6
                    }} animate={{
                      opacity: i === 0 ? 1 : 0.6,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.26,
                      ease: [0.23, 1, 0.32, 1]
                    }} className={`flex h-[48px] items-start gap-2 overflow-hidden rounded-xl border px-2.5 py-2 transition-[background-color,border-color] duration-700 ease-out ${fresh ? 'border-[#FAF8F4] bg-[#FAF8F4]' : 'border-white/10 bg-[#4A403A]'}`} data-id="element-659" data-mp="1ug390t398so9" data-mpi={i}>
                        {/* Follows the row's own state: the fresh row is white,
                         so ink marks read there — once it cools to brown the
                         same marks need their light variant or they vanish. */}
                        <BrandLogo slug={o.logo} onDark={!fresh} className="mt-[1px] size-3.5 shrink-0" data-id="element-660" data-mp="1p7u09vtwqt3z" />
                        <span className={`min-w-0 overflow-hidden text-[11.5px] leading-snug transition-colors duration-700 ease-out ${fresh ? 'font-medium text-[#2B211A]' : 'text-white/80'}`} style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2
                      }} data-id="element-661" data-mp="1fc0gpshrtzcp">
                          {o.text}
                        </span>
                      </motion.li>;
                  })}
                </AnimatePresence>
              </ul>
            </div>;
          })}
        </div>

        {/* The observation in flight, and the path it takes. */}
        <AnimatePresence data-id="element-662" data-mp="2fhuh2l2uc84s">
          {flight && <React.Fragment key={flight.key} data-id="element-663" data-mp="2kj0bqog2a5gy">
              {/* No drawn path. The card itself is the connection — it leaves
               the source and arrives at the track, and the source lighting up
               is what tells you where it came from. A dashed line just traced
               a journey that was already visible. */}
              {/* The observation in flight is a white card with dark text —
               the one genuinely new thing on a dark instrument, so it is the
               brightest object on screen, and it lands looking exactly like
               the row it becomes. */}
              <motion.div className="pointer-events-none absolute z-10 w-[220px] rounded-xl bg-[#FAF8F4] px-2.5 py-1.5 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.8)]" style={{
              left: 0,
              top: 0
            }} initial={{
              x: flight.from.x - 110,
              y: flight.from.y - 14,
              opacity: 0,
              scale: 0.96
            }} animate={{
              x: flight.to.x - 110,
              y: flight.to.y - 14,
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.98
            }} transition={{
              duration: FLIGHT_MS / 1000,
              ease: [0.23, 1, 0.32, 1],
              // Opacity is given its own short duration rather than riding the
              // 900ms travel. Sharing the flight's timing meant the card was
              // part-transparent for most of its journey and the tracks
              // underneath read straight through the text.
              opacity: {
                duration: 0.12,
                ease: 'easeOut'
              }
            }} data-id="element-664" data-mp="18ws8m5qythsa">
                <span className="flex items-start gap-2" data-id="element-665" data-mp="1xvv243jjvaqt">
                  <BrandLogo slug={flight.event.source} className="mt-[1px] size-3.5 shrink-0" data-id="element-666" data-mp="1xvd7tcgg76lj" />
                  <span className="min-w-0" data-id="element-667" data-mp="1nqdat4l5754r">
                    <span className="block text-[11.5px] font-medium leading-snug text-[#2B211A]" data-id="element-668" data-mp="1siod2e3cv30h">
                      {flight.event.text}
                    </span>
                    {flight.event.note && <span className="mt-0.5 block text-[10.5px] text-[#8C7C6E]" data-id="element-669" data-mp="1drxkv4xuq1wu">
                        {flight.event.note}
                      </span>}
                  </span>
                </span>
              </motion.div>
            </React.Fragment>}
        </AnimatePresence>
      </div>

      {/* The readings, along the bottom of the same instrument — divided by
       vertical hairlines rather than floated as four cards, and left-aligned
       under each label so they read as a panel of gauges. Counted once on
       entry: a figure that re-animates every time it is scrolled past stops
       reading as a fact. */}
      <dl className="relative grid grid-cols-2 border-t border-white/10 md:grid-cols-4" data-id="element-670" data-mp="q4pgcp88ywvv">
        {STATS.map((stat, i) => <div key={stat.label} className={`px-4 py-5 md:px-8 md:py-7 ${i > 0 ? 'border-l border-white/10' : ''} ${i === 2 ? 'border-l-0 md:border-l' : ''} ${i > 1 ? 'border-t border-white/10 md:border-t-0' : ''}`} data-id="element-671" data-mp="2sptcit6xdvdc" data-mpi={i}>
            <dt className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.1em] text-white/45 md:gap-2 md:text-[10.5px] md:tracking-[0.12em]" data-id="element-672" data-mp="2izgvhaybkkby">
              <stat.icon size={11} strokeWidth={2} aria-hidden="true" className="shrink-0" data-id="element-673" data-mp="293dljix87heg" />
              {stat.label}
            </dt>
            <dd className="font-title mt-2.5 text-[24px] font-bold leading-none tracking-tight text-white md:mt-3 md:text-[34px]" data-id="element-674" data-mp="24zpcswwuyfrm">
              <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} delay={480 + i * 90} data-id="element-675" data-mp="2040h54nylj0l" />
            </dd>
          </div>)}
      </dl>
      </div>
    </div>;
}